import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  getDocs,
  Timestamp,
  QueryDocumentSnapshot,
  DocumentData,
} from 'firebase/firestore';
import { Calendar, Search } from 'lucide-react';
import {
  startOfMonth,
  endOfMonth,
  subMonths,
  startOfQuarter,
  endOfQuarter,
  subQuarters,
} from 'date-fns';
import { Button, Table, Input, Dropdown } from '../components/ui';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/useToast';
import { db } from '../lib/firebase';
import { Session } from '../types/firestoreTypes';
import {
  SESSION_STATUS,
  SESSION_STATUS_OPTIONS,
  SessionStatus,
} from '../types/constants';
import { useTranslation } from 'react-i18next';

const SESSIONS_PER_PAGE = 10;

const DATE_FILTER_OPTIONS = [
  { value: 'all', label: 'filters.date.allTime' },
  { value: 'thisMonth', label: 'filters.date.thisMonth' },
  { value: 'lastMonth', label: 'filters.date.lastMonth' },
  { value: 'thisQuarter', label: 'filters.date.thisQuarter' },
  { value: 'lastQuarter', label: 'filters.date.lastQuarter' },
];

const SessionStatusPill: React.FC<{ status: SessionStatus }> = ({ status }) => {
  const statusStyles = {
    [SESSION_STATUS.SCHEDULED]: 'bg-[#F0F9FF] text-[#026AA2]',
    [SESSION_STATUS.COMPLETED]: 'bg-[#ECFDF3] text-[#027A48]',
    [SESSION_STATUS.CANCELLED]: 'bg-[#FEF3F2] text-[#B42318]',
    [SESSION_STATUS.NO_SHOW]: 'bg-[#FFF6ED] text-[#B93815]',
  };

  return (
    <span
      className={`inline-flex px-2 py-1 rounded-full text-sm font-medium ${statusStyles[status]}`}
    >
      {status}
    </span>
  );
};

const getDateRange = (filterValue: string) => {
  const now = new Date();
  switch (filterValue) {
    case 'thisMonth':
      return {
        start: startOfMonth(now),
        end: endOfMonth(now),
      };
    case 'lastMonth': {
      const lastMonth = subMonths(now, 1);
      return {
        start: startOfMonth(lastMonth),
        end: endOfMonth(lastMonth),
      };
    }
    case 'thisQuarter':
      return {
        start: startOfQuarter(now),
        end: endOfQuarter(now),
      };
    case 'lastQuarter': {
      const lastQuarter = subQuarters(now, 1);
      return {
        start: startOfQuarter(lastQuarter),
        end: endOfQuarter(lastQuarter),
      };
    }
    default:
      return null;
  }
};

const SessionsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addToast } = useToast();

  const [sessions, setSessions] = useState<Session[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDateFilter, setSelectedDateFilter] = useState('thisMonth');
  const [selectedStatusFilter, setSelectedStatusFilter] = useState('all');
  const [sortColumn, setSortColumn] = useState('sessionDateTime');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [lastVisible, setLastVisible] =
    useState<QueryDocumentSnapshot<DocumentData> | null>(null);
  const [hasMore, setHasMore] = useState(true);

  const fetchSessions = async (
    startAfterDoc?: QueryDocumentSnapshot<DocumentData>
  ) => {
    if (!user) return;

    try {
      startAfterDoc ? setIsFetchingMore(true) : setIsLoading(true);
      setError(null);

      let baseQuery = query(
        collection(db, 'sessions'),
        where('therapistId', '==', user.uid)
      );

      const dateRange = getDateRange(selectedDateFilter);
      if (dateRange) {
        baseQuery = query(
          baseQuery,
          where('sessionDateTime', '>=', Timestamp.fromDate(dateRange.start)),
          where('sessionDateTime', '<=', Timestamp.fromDate(dateRange.end))
        );
      }

      if (selectedStatusFilter !== 'all') {
        baseQuery = query(
          baseQuery,
          where('status', '==', selectedStatusFilter)
        );
      }

      if (searchTerm) {
        baseQuery = query(
          baseQuery,
          where('patientName', '>=', searchTerm),
          where('patientName', '<=', searchTerm + '\uf8ff')
        );
      }

      let finalQuery = query(
        baseQuery,
        orderBy(sortColumn, sortDirection),
        limit(SESSIONS_PER_PAGE)
      );

      if (startAfterDoc) {
        finalQuery = query(finalQuery, startAfter(startAfterDoc));
      }

      const snapshot = await getDocs(finalQuery);
      const newSessions = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...(doc.data() as Omit<Session, 'id'>),
      }));

      setSessions((prev) =>
        startAfterDoc ? [...prev, ...newSessions] : newSessions
      );
      setLastVisible(snapshot.docs[snapshot.docs.length - 1] || null);
      setHasMore(snapshot.docs.length === SESSIONS_PER_PAGE);
    } catch (err) {
      console.error('Error fetching sessions:', err);
      setError(t('toasts.errorLoadingSessions'));
      addToast({
        type: 'error',
        message: t('toasts.errorLoadingSessions'),
      });
    } finally {
      setIsLoading(false);
      setIsFetchingMore(false);
    }
  };

  useEffect(() => {
    setLastVisible(null);
    setSessions([]);
    setHasMore(true);
    fetchSessions();
  }, [
    user,
    selectedDateFilter,
    selectedStatusFilter,
    searchTerm,
    sortColumn,
    sortDirection,
  ]);

  const handleLoadMore = async () => {
    if (!hasMore || !lastVisible || isLoading || isFetchingMore) return;
    await fetchSessions(lastVisible);
  };

  const handleSortChange = (column: string, direction: 'asc' | 'desc') => {
    setSortColumn(column);
    setSortDirection(direction);
  };

  const handleRowClick = (session: Session) => {
    navigate(`/patients/${session.patientId}/sessions/${session.id}`);
  };

  const columns = [
    {
      key: 'patientName',
      header: t('table.sessions.patient'),
      sortable: false,
    },
    {
      key: 'sessionDateTime',
      header: t('table.sessions.dateTime'),
      sortable: true,
      render: (session: Session) =>
        session.sessionDateTime.toDate().toLocaleString(),
    },
    {
      key: 'status',
      header: t('table.sessions.status'),
      sortable: false,
      render: (session: Session) => (
        <SessionStatusPill status={session.status} />
      ),
    },
    {
      key: 'sessionType',
      header: t('table.sessions.type'),
      sortable: false,
    },
    {
      key: 'price',
      header: t('table.sessions.price'),
      render: (session: Session) =>
        `${session.price.currency} ` +
        (session.price.currency === 'HUF'
          ? Math.round(session.price.amount)
          : session.price.amount.toFixed(2)),
    },
    {
      key: 'notes',
      header: t('table.sessions.notes'),
      render: (session: Session) => (
        <span title={session.notes} className="truncate block max-w-xs">
          {session.notes || '-'}
        </span>
      ),
    },
  ];

  if (error) {
    return <div className="p-4 bg-red-50 rounded-lg text-red-700">{error}</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <h1 className="text-2xl font-semibold text-[#101828]">
          {t('table.sessions.title')}
        </h1>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-[#EAECF0] p-6">
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <Input
            placeholder={t('filters.searchByPatient')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            leftIcon={<Search className="w-5 h-5" />}
            className="w-full sm:w-80"
          />

          <Dropdown
            options={DATE_FILTER_OPTIONS.map((opt) => ({
              ...opt,
              label: t(opt.label),
            }))}
            value={selectedDateFilter}
            onChange={setSelectedDateFilter}
            placeholder={t('filters.dateRange')}
            className="w-full sm:w-48"
          />

          <Dropdown
            options={SESSION_STATUS_OPTIONS.map((opt) => ({
              ...opt,
              label: t(`status.${opt.label}`),
            }))}
            value={selectedStatusFilter}
            onChange={setSelectedStatusFilter}
            placeholder={t('filters.status')}
            className="w-full sm:w-48"
          />
        </div>

        <Table
          columns={columns}
          data={sessions}
          isLoading={isLoading}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          onSortChange={handleSortChange}
          onRowClick={handleRowClick}
        />

        {hasMore && !isLoading && !isFetchingMore && (
          <div className="mt-4 text-center">
            <Button
              variant="secondary"
              onClick={handleLoadMore}
              isLoading={isFetchingMore}
              disabled={isFetchingMore}
            >
              {t('table.loadMore')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SessionsPage;
